import React, { useState, useEffect } from "react";
import {
  DEACTIVATE_USER,
  UPDATE_USER_DEFAULT_COLOR,
} from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import "./listAllUsers.css";
import { useMutation, useLazyQuery } from "@apollo/client";
import DeleteIcon from "@mui/icons-material/Delete";
import { GET_ALL_USERS } from "../../../../utils/GraphQL/queries";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import WageModal from "./WageModal";
import LegalNameModal from "./LegalNameModal";
import { lockBodyScroll, releaseBodyScroll } from "../../../../utils/bodyScrollLock";

const COLOR_OPTIONS = [
  { hex: "#D50000", label: "Tomato" },
  { hex: "#E67C73", label: "Flamingo" },
  { hex: "#F4511E", label: "Tangerine" },
  { hex: "#F6BF26", label: "Banana" },
  { hex: "#33B679", label: "Sage" },
  { hex: "#0B8043", label: "Basil" },
  { hex: "#039BE5", label: "Peacock" },
  { hex: "#3F51B5", label: "Blueberry" },
  { hex: "#7986CB", label: "Lavender" },
  { hex: "#8E24AA", label: "Grape" },
  { hex: "#616161", label: "Graphite" },
];

const ListAllUsers = () => {
  const { handleSnack } = useSnackbar();
  const [openWageModal, setOpenWageModal] = useState(false);
  const [openLegalNameModal, setOpenLegalNameModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersData, setUsersData] = useState([]);
  const [colorPickerUserId, setColorPickerUserId] = useState(null);
  const [deactivateUserProf] = useMutation(DEACTIVATE_USER);
  const [updateDefaultColor] = useMutation(UPDATE_USER_DEFAULT_COLOR);
  const [fetchUsers] = useLazyQuery(GET_ALL_USERS, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    getUsersInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* See utils/bodyScrollLock.js: release lives in the cleanup so the pair is
     balanced, and the two flags collapse to one boolean so switching straight
     from one modal to the other does not release and re-lock. */
  const anyUserModalOpen = !!(openWageModal || openLegalNameModal);
  useEffect(() => {
    if (!anyUserModalOpen) return undefined;
    lockBodyScroll();
    return () => releaseBodyScroll();
  }, [anyUserModalOpen]);

  const getUsersInfo = async () => {
    const { data } = await fetchUsers();
    if (data?.getAllUsers?.length > 0) {
      setUsersData([...data?.getAllUsers]);
      setUsersLoading(false);
      return;
    }

    setUsersLoading(false);
  };

  const deactivateUser = async (userId) => {
    const deactivated = await deactivateUserProf({
      variables: { idDelete: userId },
      refetchQueries: [{ query: GET_ALL_USERS }],
    });

    if (deactivated) {
      handleSnack("Account Deactivated", 3000);
    }
  };

  const closeWageModal = (event) => {
    if (event) {
      event.stopPropagation();
    }

    setOpenWageModal(false);
    setSelectedUser({});
  };

  const closeLegalNameModal = (event) => {
    if (event) {
      event.stopPropagation();
    }

    setOpenLegalNameModal(false);
    setSelectedUser({});
  };

  // Patch local state — refetchQueries does NOT update this list (it renders
  // from useState filled once at mount), see updateUserWageCB pattern.
  const updateUserLegalNameCB = ({ userId, legalFirstName, legalLastName }) => {
    setUsersData((prev) =>
      prev.map((u) =>
        u._id === userId ? { ...u, legalFirstName, legalLastName } : u,
      ),
    );
  };

  const updateUserWageCB = ({ userId, wage, seniority, payPercentage }) => {
    setUsersData((prev) => {
      const updatedArr = prev?.map((user) => {
        if (userId !== user._id) {
          return user;
        }

        const seniorityVal =
          seniority !== "" && seniority != null
            ? parseInt(seniority)
            : user.seniority;
        const payPctVal =
          payPercentage !== "" && payPercentage != null
            ? payPercentage
            : user.payPercentage;

        return {
          ...user,
          hourlyWage: wage,
          seniority: seniorityVal,
          payPercentage: payPctVal,
        };
      });

      return [...updatedArr];
    });
  };

  const handleColorSelect = async (userId, hex) => {
    setColorPickerUserId(null);
    try {
      const { data } = await updateDefaultColor({
        variables: { userId, defaultColor: hex },
      });

      if (data?.updateUserDefaultColor) {
        setUsersData((prev) =>
          prev.map((u) => (u._id === userId ? { ...u, defaultColor: hex } : u)),
        );
        handleSnack("Color updated", 2000);
      }
    } catch {
      handleSnack("Failed to update color", 3000);
    }
  };

  return (
    <div className="lau-container">
      {usersLoading && <AdminBDRSpinner message={"Loading..."} />}
      {openWageModal && (
        <WageModal
          selectedUser={selectedUser}
          closeWageModal={closeWageModal}
          handleSnack={handleSnack}
          updateUserWageCB={updateUserWageCB}
        />
      )}
      {openLegalNameModal && (
        <LegalNameModal
          selectedUser={selectedUser}
          closeModal={closeLegalNameModal}
          handleSnack={handleSnack}
          updateUserLegalNameCB={updateUserLegalNameCB}
        />
      )}

      <div className="lau-grid">
        {usersData?.map((user, index) => {
          const cardClass =
            selectedUser?._id === user?._id
              ? "lau-user-card bdr-gradient-panel selected-listed-user"
              : "lau-user-card bdr-gradient-panel";

          const perms = (user?.permission || "")
            .split(",")
            .map((r) => r.trim().toLowerCase());
          const isRefinisher = perms.includes("refinisher");

          return (
            <div className={cardClass} key={user?.firstName + index}>
              <div className="lau-user-info">
                <div className="lau-user-name-row">
                  <h3 className="lau-user-name">
                    {user?.firstName} {user?.lastName}
                  </h3>
                  <div className="user-color-picker-wrapper">
                    <button
                      className="user-color-btn"
                      onClick={() =>
                        setColorPickerUserId(
                          colorPickerUserId === user._id ? null : user._id,
                        )
                      }
                    >
                      <span
                        className="user-color-circle"
                        style={{
                          backgroundColor: user?.defaultColor || "#888",
                        }}
                      />
                      Color:{" "}
                      {COLOR_OPTIONS.find((c) => c.hex === user?.defaultColor)
                        ?.label ?? "Not Assigned"}
                    </button>
                    {colorPickerUserId === user._id && (
                      <div className="user-color-dropdown">
                        {COLOR_OPTIONS.map((c) => (
                          <button
                            key={c.hex}
                            className="user-color-option"
                            onClick={() => handleColorSelect(user._id, c.hex)}
                          >
                            <span
                              className="user-color-circle"
                              style={{ backgroundColor: c.hex }}
                            />
                            <span>{c.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {user?.legalFirstName && user?.legalLastName && (
                  <h5 className="lau-user-meta" style={{ opacity: 0.7 }}>
                    Legal: {user.legalFirstName} {user.legalLastName}
                  </h5>
                )}
                <h5 className="lau-user-meta">
                  Perm:{" "}
                  {user?.permission
                    ?.split(",")
                    .map((r) => r.trim())
                    .join(" | ")}
                </h5>
                <h5 className="lau-user-meta lau-user-email">
                  Email: {user?.email}
                </h5>
                {isRefinisher
                  ? user?.payPercentage && (
                      <h5 className="lau-user-meta">
                        Pay %: {user?.payPercentage}
                      </h5>
                    )
                  : user?.hourlyWage &&
                    user.hourlyWage !== "0" && (
                      <h5 className="lau-user-meta">
                        Wage: {priceStrFormatter(user?.hourlyWage)}
                      </h5>
                    )}
                {user?.seniority != null && user?.seniority !== "" && (
                  <h5 className="lau-user-meta">
                    Seniority: {user?.seniority}
                  </h5>
                )}
              </div>
              <div className="lau-actions-box">
                <button
                  className="user-deactivate-btn"
                  onClick={() => {
                    setSelectedUser(user);
                    setOpenWageModal(true);
                  }}
                >
                  Edit Wage
                </button>
                <button
                  className="user-deactivate-btn"
                  onClick={() => {
                    setSelectedUser(user);
                    setOpenLegalNameModal(true);
                  }}
                >
                  Legal Name
                </button>
                <button
                  className="user-deactivate-btn"
                  onClick={() => {
                    handleSnack(
                      <div className="user-deactivate-confirm">
                        <span className="user-deactivate-text-confirm">
                          deactivate {user?.firstName}'s Profile?
                        </span>
                        <button
                          className="user-deactivate-btn"
                          onClick={() => {
                            handleSnack(
                              <div className="user-deactivate-confirm">
                                <span className="user-deactivate-text-confirm">
                                  Are you sure?
                                </span>
                                <button
                                  className="user-deactivate-btn"
                                  onClick={() => {
                                    deactivateUser(user._id);
                                  }}
                                >
                                  <DeleteIcon
                                    sx={{
                                      fontSize: "20px",
                                      color: "#b40000",
                                    }}
                                  />
                                  (Deactivate)
                                </button>
                              </div>,
                              10000,
                            );
                          }}
                        >
                          <DeleteIcon
                            sx={{ fontSize: "20px", color: "#b40000" }}
                          />
                          (Deactivate)
                        </button>
                      </div>,
                      10000,
                    );
                  }}
                >
                  <DeleteIcon sx={{ fontSize: "20px", color: "#b40000" }} />
                  (Deactivate)
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ListAllUsers;
